<script setup lang="ts">
import { useGetPublicReviewsForRakuten } from './useGetPublicReviewsForRakuten';
import { convertPersonalityIdToDiagnosisResultPersonalityType } from '@/types/app/diagnosis/personality';
import { getUserProfileStringLegacy } from '@/utils/userProfile';
import UserThumbnail from 'theme/components/atoms/UserThumbnail.vue';
import RatingStars from 'theme/components/molecules/RatingStars.vue';
import PlannerThumbnail from '~/components/organisms/PlannerThumbnail/PlannerThumbnail.vue';

const reviews = useGetPublicReviewsForRakuten();
</script>

<template>
  <div :class="$style.container">
    <h2 :class="$style.heading">
      他の人のクチコミがわかるので<br />安心して相談ができます
    </h2>
    <div :class="$style.annotation">
      ※2022年4~6月に評価の高かったクチコミから抜粋
    </div>
    <ClientOnly>
      <div :class="$style['reviews-container']">
        <div
          v-for="r in reviews"
          :key="r.review"
          :class="[$style['reviews-container__item'], $style.review]"
        >
          <div :class="$style['review-user']">
            <UserThumbnail
              :personality-type="
                convertPersonalityIdToDiagnosisResultPersonalityType(
                  r.user.personalityId
                )
              "
              :class="$style['review-user__avatar']"
            />
            <div :class="$style['review-user__personality']">
              {{ getUserProfileStringLegacy(r.user.sex, r.user.birthYear) }}
            </div>
            <RatingStars
              :rate="r.score"
              :show-rate-number="true"
              :class="$style['review-user__rate']"
            />
          </div>
          <div :class="$style.review__body">
            {{ r.review }}
          </div>
          <hr :class="$style.review__divider" />
          <div :class="$style['review-planner']">
            <PlannerThumbnail
              :img-path="r.planner.avatarUrl"
              :class="$style['review-planner__avatar']"
            />
            <div>
              <div>相談したプランナー</div>
              <NuxtLink
                :to="{
                  path: `/planner/${r.planner.id}`,
                }"
                >{{ r.planner.name }}</NuxtLink
              >
            </div>
          </div>
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<style lang="scss" module>
.container {
  padding: 40px 0 72px;

  @include tablet-upper {
    padding: 80px 0 124px;
  }
}

.heading {
  font-size: 32px;
  text-align: center;

  @include tablet-upper {
    font-size: 44px;
  }
}

.annotation {
  margin-top: 12px;
  font-size: min(18px, 3.4vw);
  text-align: center;

  @include desktop-only {
    font-weight: bold;
  }
}

.reviews-container {
  margin: 24px auto 0 auto;
  padding: 0 20px;
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  max-width: 420px;

  &__item {
    flex: 1;
  }

  @include desktop-only {
    flex-direction: row;
    align-items: stretch;
    margin-top: 36px;
    gap: 36px;
    max-width: 1128px;
  }
}

.review {
  border-radius: 12px;
  border: 1px solid $color-border-medium-emphasis;
  display: flex;
  gap: 16px;
  flex-direction: column;
  padding: 16px 12px;

  &__divider {
    border: none;
    background-color: $color-border-medium-emphasis;
    height: 1px;
  }

  &__body {
    height: auto;
    flex: 1;
  }
}

.review-user {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 0 4px;

  &__avatar {
    width: 40px;
    height: 40px;
  }

  &__personality {
    font-weight: bold;
  }

  &__rate {
    flex: 1;
    display: flex;
    justify-content: center;
    font-size: 0.9em;
  }
}

.review-planner {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  padding: 0 4px;

  &__avatar {
    width: 40px;
    height: 40px;
  }
}
</style>
